// Strumenti: cosa possono usare gli agenti. Spazio di lavoro e rete ci sono sempre; le connessioni
// (memo, server MCP, Gmail, Pushover, API) si aggiungono dal market «Aggiungi strumento». Dalla
// 0.2.6 sono raggruppate per tipo, un accordion per tipo; di ogni connessione si sceglie quali
// strumenti concedere agli agenti.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../../lib/api';
import type { PendingTool, ToolCatalog, ToolGroup } from '../../lib/api';
import { Banner } from '../../components/Banner';
import { Button } from '../../components/Button';
import { KindGroup, groupBy } from '../../components/KindGroup';
import { PageHeader } from '../../components/PageHeader';
import { useStore } from '../../store/StoreProvider';
import { AddTool } from './AddTool';
import { RecipientsModal } from './RecipientsModal';
import { ICON, TINT, ToolCard } from './ToolCard';

export function Tools() {
  const { openModal, toast } = useStore();
  const [data, setData] = useState<ToolCatalog | null>(null);
  // il pannello aperto: nuovo, o la ripresa dell'abbinamento in attesa scelto
  const [adding, setAdding] = useState<{ resume?: PendingTool } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<ToolGroup | null>(null);
  const [params, setParams] = useSearchParams();

  const refresh = useCallback(async () => {
    try {
      setData(await api.tools());
      setError(null);
    } catch {
      setError('Non riesco a leggere gli strumenti.');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // il ritorno da Google: l'esito arriva nell'indirizzo, si dice e si toglie
  useEffect(() => {
    const linked = params.get('linked');
    const failed = params.get('error');
    if (!linked && !failed) return;
    toast(
      linked
        ? {
            kind: 'success',
            title: `${linked} collegato`,
            body: 'Scegli quali strumenti concedere.',
          }
        : { kind: 'danger', title: 'Gmail non collegato', body: failed ?? '' },
    );
    setParams({}, { replace: true });
  }, [params, setParams, toast]);

  const linked = useMemo(() => {
    const out: Record<string, number> = {};
    for (const g of data?.groups ?? []) if (g.removable) out[g.kind] = (out[g.kind] ?? 0) + 1;
    return out;
  }, [data]);

  const remove = (g: ToolGroup) =>
    openModal({
      kind: 'confirm',
      title: `Scollegare ${g.name}?`,
      okLabel: 'Scollega',
      danger: true,
      body: 'I Pops non potranno più usarne gli strumenti. La chiave va revocata anche sul server.',
      payload: {
        run: async () => {
          await api.removeTool(g.id);
          await refresh();
        },
      },
    });

  // un'azione sulla pagina: fatta, si rilegge; rifiutata, lo si dice
  const act = (work: Promise<unknown>) =>
    work.then(refresh, (e: unknown) =>
      toast({
        kind: 'danger',
        title: 'Non è riuscito',
        body: e instanceof ApiError && e.detail ? e.detail : 'Riprova.',
      }),
    );

  const reconnect = (g: ToolGroup) =>
    void api.reconnectGoogle(g.id, window.location.origin).then(
      (r) => window.location.assign(r.authorize),
      (e: unknown) =>
        toast({
          kind: 'danger',
          title: 'Non riesco a ricollegare',
          body: e instanceof ApiError && e.detail ? e.detail : 'Riprova.',
        }),
    );

  const toggle = (g: ToolGroup, name: string, on: boolean) => {
    const enabled = g.tools.filter((t) => (t.name === name ? on : t.enabled)).map((t) => t.name);
    void act(api.changeTool(g.id, { enabled }));
  };

  return (
    <div className="page">
      <PageHeader
        title="Strumenti"
        subtitle="Cosa possono usare i Pops, raggruppato per tipo. Ogni strumento si collega a modo suo."
        actions={
          <Button variant="primary" icon="plus" onClick={() => setAdding({})}>
            Aggiungi strumento
          </Button>
        }
      />
      {error && (
        <div style={{ marginBottom: 16 }}>
          <Banner kind="danger">{error}</Banner>
        </div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr)', gap: 20 }}>
        {data?.pending.map((p) => (
          <div key={p.id} className="tool-card" style={{ padding: 16 }}>
            <div className="t-body" style={{ fontWeight: 600 }}>
              {p.url} · in attesa di approvazione, codice <code>{p.userCode}</code>
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <Button variant="secondary" onClick={() => setAdding({ resume: p })}>
                Continua
              </Button>
              <Button variant="ghost" onClick={() => void act(api.cancelTool(p.id))}>
                Annulla
              </Button>
            </div>
          </div>
        ))}
        {groupBy(data?.groups ?? [], (g) => g.kind).map(([kind, items]) => {
          const builtin = !items.some((g) => g.removable);
          const tools = items.flatMap((g) => g.tools);
          const granted = tools.filter((t) => t.enabled).length;
          const n = items.length;
          return (
            <KindGroup
              key={kind}
              title={data?.kinds.find((k) => k.id === kind)?.label ?? items[0].name}
              icon={ICON[kind]}
              tint={TINT[kind] ?? 'lilac'}
              count={builtin ? 'Sempre attivo' : `${n} ${n === 1 ? 'connessione' : 'connessioni'}`}
              summary={`${tools.length} ${tools.length === 1 ? 'strumento' : 'strumenti'} · ${granted} ${granted === 1 ? 'concesso' : 'concessi'} ai Pops`}
            >
              {items.map((g) => (
                <ToolCard
                  key={g.id}
                  group={g}
                  onRemove={() => remove(g)}
                  onRefresh={() => void act(api.refreshTool(g.id))}
                  onToggle={(name, on) => toggle(g, name, on)}
                  onDescribe={(text) => void act(api.changeTool(g.id, { description: text }))}
                  onParams={(text) => void act(api.changeTool(g.id, { params: text }))}
                  onReconnect={() => reconnect(g)}
                  onRecipients={() => setEditing(g)}
                />
              ))}
            </KindGroup>
          );
        })}
      </div>
      {adding && data && (
        <AddTool
          kinds={data.kinds}
          linked={linked}
          resume={adding.resume}
          onClose={() => {
            setAdding(null);
            void refresh();
          }}
        />
      )}
      {editing && (
        <RecipientsModal
          group={editing}
          onClose={() => {
            setEditing(null);
            void refresh();
          }}
        />
      )}
    </div>
  );
}
